'use client'
import { useState } from 'react'

import type { AccordionsSectionFragment } from '@/lib/types/datocms/graphql/generated'
import { TextWithHighlight } from '../TextWithHighlight'
import Eyebrow from '../ui/Eyebrow'
import PlusMinus from '../svg/icon/PlusMinus'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'

import clsx from 'clsx'
import { StructuredText } from 'react-datocms'

export default function AccordionsSection({
	title,
	tag,
	accordions,
	screen,
}: Readonly<AccordionsSectionFragment>) {
	const [activeAccordion, setActiveAccordion] = useState<string | null>(null)

	const handleAccordionClick = (id: string) => {
		if (activeAccordion === id) {
			setActiveAccordion(null)
		} else {
			setActiveAccordion(id)
		}
	}

	return (
		<section className="accordions-section @container">
			<div
				className={clsx(
					'relative flex common-x-padding max-w-screen-2xl mx-auto py-16 @3xl:py-20',
					screen === 'full' ? 'flex-col' : 'flex-col @3xl:flex-row gap-x-10 @5xl:gap-x-20'
				)}
			>
				<div className={clsx(screen !== 'full' && '@3xl:sticky top-24', 'w-full h-fit')}>
					{tag ? (
						<div className="mb-4 @3xl:mb-10">
							<Eyebrow>{tag}</Eyebrow>
						</div>
					) : null}

					{title && !isStructuredTextEmpty(title.text) && (
						<div className="heading-size-3 max-w-xl">
							<TextWithHighlight text={title} />
						</div>
					)}
				</div>

				<div className="flex flex-col gap-4 mt-16 w-full">
					{accordions.map((accordion) => (
						<div key={accordion.id as string} className="bg-white-frosted rounded-lg">
							<button
								className={clsx(
									'flex justify-between gap-2 w-full py-6 px-4 @5xl:px-6 @5xl:py-8 text-left bg-white-frosted hover:bg-white-frosted-shade transition-colors duration-300 ease-out',
									activeAccordion === accordion.id
										? 'rounded-t-lg bg-white-frosted-shade'
										: 'rounded-lg'
								)}
								onClick={() => handleAccordionClick(accordion.id as string)}
							>
								{accordion.question && (
									<h3 className="text-2xl font-medium max-w-2xl">{accordion.question}</h3>
								)}
								<PlusMinus isOpen={activeAccordion === accordion.id} className="mt-1" />
							</button>
							{accordion.answer && !isStructuredTextEmpty(accordion.answer) && (
								<div
									className={`grid text-sm text-slate-600 overflow-hidden px-4 transition-all duration-300 ease-out ${activeAccordion === accordion.id ? 'grid-rows-[1fr] opacity-100 py-4' : 'grid-rows-[0fr] opacity-0'}`}
								>
									<div className="rich-text overflow-hidden max-w-2xl text-lg">
										<StructuredText data={accordion.answer} />
									</div>
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</section>
	)
}
